
 "use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useLanguage } from "../context/LanguageContext";

/* ==================================================================
   ��  TEST VIDEO — works instantly
   ================================================================== */
const STATIC_TEST_VIDEO =
  "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4";
  // Or your local file (after copying to public/):
  // "/videotest.mp4";

interface ImageItem {
  id: number;
  url: string;
}

interface HeroData {
  title: string;
  subtitle: string;
  cta_primary: string;
  cta_secondary: string;
  images: ImageItem[];
  video_url?: string | null;
}

const featureTranslations = {
  fr: {
    distributors_title: "Des distributeurs",
    distributors_sub: "proches de chez vous",
    question_title: "Une question ?",
    question_sub: "Contactez-nous",
    slide_label: "Diapositive",
    watch_video: "Voir la vidéo",
    close_video: "Fermer",
  },
  en: {
    distributors_title: "Distributors",
    distributors_sub: "near you",
    question_title: "A question?",
    question_sub: "Contact us",
    slide_label: "Slide",
    watch_video: "Watch video",
    close_video: "Close",
  },
  ar: {
    distributors_title: "موزعون",
    distributors_sub: "قريبون منك",
    question_title: "هل لديك سؤال؟",
    question_sub: "اتصل بنا",
    slide_label: "شريحة",
    watch_video: "شاهد الفيديو",
    close_video: "إغلاق",
  },
};

const AUTOPLAY_MS = 6500;

/* ------------------------------------------------------------------ */
/*  Icons                                                             */
/* ------------------------------------------------------------------ */
function MapPinIcon() {
  return (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 21s-7-5.686-7-11a7 7 0 1 1 14 0c0 5.314-7 11-7 11Z" />
      <circle cx="12" cy="10" r="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function QuestionIcon() {
  return (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.5 9.5a2.5 2.5 0 0 1 4.7 1c0 1.5-2.2 2-2.2 3" />
      <circle cx="12" cy="17" r="0.6" fill="currentColor" />
    </svg>
  );
}

function PlayIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M8 5.14v13.72a1 1 0 0 0 1.54.84l10.4-6.86a1 1 0 0 0 0-1.68L9.54 4.3A1 1 0 0 0 8 5.14Z" />
    </svg>
  );
}

function CloseIcon({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M6 18L18 6" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Feature pill                                                       */
/* ------------------------------------------------------------------ */
function FeaturePill({
  icon,
  textone,
  texttwo,
  href,
}: {
  icon: "map" | "question";
  textone: string;
  texttwo: string;
  href?: string;
}) {
  const inner = (
    <>
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white/10 text-white ring-1 ring-white/10 transition-all duration-300 group-hover:bg-white/20 group-hover:ring-white/20">
        {icon === "map" ? <MapPinIcon /> : <QuestionIcon />}
      </span>
      <span className="min-w-0 text-start">
        <span className="block text-[12px] font-semibold leading-tight text-white">{textone}</span>
        <span className="mt-0.5 block text-[10px] leading-tight text-white/55">{texttwo}</span>
      </span>
    </>
  );

  const base =
    "group flex items-center gap-2.5 rounded-2xl border border-white/10 bg-white/[0.04] px-2.5 py-2 backdrop-blur-md transition-all duration-300 hover:border-white/20 hover:bg-white/[0.09]";

  if (href) return <Link href={href} className={base}>{inner}</Link>;
  return <div className={base}>{inner}</div>;
}

/* ------------------------------------------------------------------ */
/*  Small Inline Video Player                                          */
/* ------------------------------------------------------------------ */
function InlineVideoPlayer({
  src,
  onClose,
  isRTL,
  closeLabel,
}: {
  src: string;
  onClose: () => void;
  isRTL: boolean;
  closeLabel: string;
}) {
  const isEmbed = /youtube|youtu\.be|vimeo|dailymotion/i.test(src);

  return (
    <div className="relative w-full max-w-md overflow-hidden rounded-2xl border border-white/15 bg-black/60 shadow-2xl shadow-black/50 backdrop-blur-md">
      {/* Close button */}
      <button
        onClick={onClose}
        aria-label={closeLabel}
        className="absolute end-2 top-2 z-20 grid h-8 w-8 place-items-center rounded-full border border-white/25 bg-black/60 text-white backdrop-blur-md transition-all duration-200 hover:scale-110 hover:bg-black/80"
      >
        <CloseIcon className="h-4 w-4" />
      </button>

      {/* Small header */}
      <div className="flex items-center gap-2 px-3 pt-2 pb-1.5">
        <span className="h-px w-5 bg-white/50" />
        <span className="text-[9px] font-medium uppercase tracking-[0.25em] text-white/60">
          {isRTL ? "فيديو الشركة" : "Corporate Video"}
        </span>
      </div>

      {/* 16:9 video box — SMALL */}
      <div className="relative aspect-video w-full">
        {isEmbed ? (
          <iframe
            src={src}
            title="Corporate video"
            className="absolute inset-0 h-full w-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <video
            src={src}
            controls
            autoPlay
            playsInline
            className="absolute inset-0 h-full w-full object-contain"
          />
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Hero                                                               */
/* ------------------------------------------------------------------ */
export default function Hero() {
  const { language } = useLanguage();
  const [data, setData] = useState<HeroData | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const touchStartX = useRef<number | null>(null);

  const isRTL = language === "ar";
  const t = featureTranslations[language as "fr" | "en" | "ar"] ?? featureTranslations.en;

  /* ---------------------------- data ---------------------------- */
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/HomePage?locale=${language}`, { cache: "no-store" });
        const json = await res.json();
        if (cancelled) return;

        const item = json?.data?.[0];

        if (item) {
          const images: ImageItem[] = (item.image ?? []).map((img: any) => ({
            id: img.id,
            url: `${process.env.NEXT_PUBLIC_STRAPI_URL}${img.url}`,
          }));

          const rawVideo = item.video?.url || item.video_url || item.video?.[0]?.url || null;
          const apiVideo = rawVideo
            ? rawVideo.startsWith("http")
              ? rawVideo
              : `${process.env.NEXT_PUBLIC_STRAPI_URL}${rawVideo}`
            : null;

          setData({
            title: item.title,
            subtitle: item.subtitle,
            cta_primary: item.cta_primary,
            cta_secondary: item.cta_secondary,
            images,
            video_url: apiVideo || STATIC_TEST_VIDEO,
          });
        } else {
          setData({
            title: "Welcome",
            subtitle: "",
            cta_primary: "Explore",
            cta_secondary: "About",
            images: [],
            video_url: STATIC_TEST_VIDEO,
          });
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setCurrentIndex(0);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [language]);

  /* -------------------------- autoplay -------------------------- */
  const images = data?.images ?? [];
  const hasMultiple = images.length > 1;

  useEffect(() => {
    if (!hasMultiple || paused || showVideo) return;
    const id = window.setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, AUTOPLAY_MS);
    return () => window.clearInterval(id);
  }, [hasMultiple, paused, images.length, showVideo]);

  /* --------------------------- controls ------------------------- */
  const goToSlide = (index: number) => setCurrentIndex(index);
  const nextSlide = () => setCurrentIndex((p) => (p + 1) % images.length);
  const prevSlide = () => setCurrentIndex((p) => (p - 1 + images.length) % images.length);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) (delta > 0 ? prevSlide : nextSlide)();
    touchStartX.current = null;
  };

  /* ---------------------------- states -------------------------- */
  if (loading) {
    return (
      <section className="relative flex h-screen h-[100svh] min-h-[620px] w-full items-center justify-center bg-neutral-950">
        <div className="flex flex-col items-center gap-5">
          <span className="h-10 w-10 animate-spin rounded-full border-2 border-white/15 border-t-white" />
          <span className="text-[11px] font-medium uppercase tracking-[0.25em] text-white/50">Loading</span>
        </div>
      </section>
    );
  }

  if (!data) {
    return (
      <section className="relative flex h-screen h-[100svh] min-h-[620px] w-full items-center justify-center bg-neutral-950 text-white">
        <div className="text-center">
          <div className="mb-4 text-5xl opacity-60">��</div>
          <p className="text-sm uppercase tracking-[0.25em] text-white/50">{language}</p>
        </div>
      </section>
    );
  }

  const activeNumber = String(currentIndex + 1).padStart(2, "0");
  const totalNumber = String(Math.max(images.length, 1)).padStart(2, "0");

  return (
    <section
      className="relative h-screen h-[100svh] min-h-[660px] w-full overflow-hidden bg-neutral-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* slides background */}
      <div className="absolute inset-0">
        {images.map((img, i) => (
          <div
            key={img.id ?? i}
            aria-hidden={i !== currentIndex}
            className={`absolute inset-0 transition-opacity duration-[1400ms] ease-out ${
              i === currentIndex ? "opacity-100" : "opacity-0"
            }`}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={img.url}
              alt=""
              loading={i === 0 ? "eager" : "lazy"}
              className={`h-full w-full object-cover transition-transform duration-[9000ms] ease-out ${
                i === currentIndex ? "scale-110" : "scale-100"
              }`}
              style={{ filter: "brightness(1.08) contrast(1.05)" }}
            />
          </div>
        ))}
      </div>

      {/* overlays */}
      <div className={`absolute inset-0 ${isRTL ? "bg-gradient-to-l" : "bg-gradient-to-r"} from-black/65 via-black/20 to-transparent`} />
      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/55 to-transparent" />

      {/* content */}
      <div dir={isRTL ? "rtl" : "ltr"} className="relative z-10 flex h-full w-full items-center">
        <div className="mx-auto w-full max-w-7xl px-5 sm:px-8 lg:px-12">
          <div className={`grid w-full grid-cols-1 items-center gap-8 lg:grid-cols-12 ${isRTL ? "text-right" : "text-left"}`}>
            {/* ---------- LEFT: text content ---------- */}
            <div className="lg:col-span-7 xl:col-span-6">
              <div className="relative rounded-3xl border border-white/15 bg-transparent p-6 backdrop-blur-[2px] sm:p-8 lg:p-10">
                <div className="mb-5 flex items-center gap-3">
                  <span className="h-px w-8 bg-white/70" />
                  <span className="text-[11px] font-medium uppercase tracking-[0.3em] text-white/90">
                    {t.slide_label} {activeNumber}
                  </span>
                </div>

                <h1
                  className="text-4xl font-semibold leading-[1.05] tracking-tight text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.6)] sm:text-5xl lg:text-6xl xl:text-7xl"
                  style={{ textWrap: "balance" }}
                >
                  {data.title}
                </h1>

                <p className="mt-5 max-w-xl text-base leading-relaxed text-white/90 drop-shadow-[0_1px_8px_rgba(0,0,0,0.6)] sm:text-lg">
                  {data.subtitle}
                </p>

                {/* CTAs */}
                <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                  <a
                    href="#specialties"
                    className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-white px-6 py-3 text-sm font-semibold text-neutral-900 shadow-lg shadow-black/40 transition-all duration-300 hover:bg-white/90 hover:shadow-xl active:scale-[0.98]"
                  >
                    <span>{data.cta_primary}</span>
                    <svg
                      className={`h-4 w-4 transition-transform duration-300 ${isRTL ? "rotate-180 group-hover:-translate-x-1" : "group-hover:translate-x-1"}`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </a>

                  <Link
                    href="/about"
                    className="group inline-flex items-center justify-center gap-2.5 rounded-full border border-white/60 bg-transparent px-6 py-3 text-sm font-semibold text-white backdrop-blur-sm transition-all duration-300 hover:border-white/90 hover:bg-white/10 active:scale-[0.98]"
                  >
                    <span>{data.cta_secondary}</span>
                    <svg
                      className={`h-4 w-4 transition-transform duration-300 ${isRTL ? "rotate-180 group-hover:-translate-x-1" : "group-hover:translate-x-1"}`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>

                  {/* WATCH VIDEO — small button 
                  <button
                    type="button"
                    onClick={() => setShowVideo((v) => !v)}
                    aria-label={t.watch_video}
                    className="group inline-flex items-center justify-center gap-2.5 rounded-full border border-white/25 bg-white/[0.06] px-4 py-2.5 text-sm font-semibold text-white backdrop-blur-md transition-all duration-300 hover:border-white/50 hover:bg-white/[0.12] active:scale-[0.98]"
                  >
                    <span className="grid h-7 w-7 place-items-center rounded-full bg-white text-neutral-900 transition-transform duration-300 group-hover:scale-110">
                      <PlayIcon className="h-3.5 w-3.5 translate-x-[1px]" />
                    </span>
                    <span className="whitespace-nowrap">
                      {showVideo ? t.close_video : t.watch_video}
                    </span>
                  </button>*/}
                </div>

                {/* feature pills mobile */}
                <div className="mt-6 grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:hidden">
                  <FeaturePill icon="map" textone={t.distributors_title} texttwo={t.distributors_sub} href="/locate-us" />
                  <FeaturePill icon="question" textone={t.question_title} texttwo={t.question_sub} href="/contact" />
                </div>
              </div>
            </div>

            {/* ---------- RIGHT: SMALL video OR big number ---------- */}
            <div className="lg:col-span-5 xl:col-span-6">
              {/* Video inline (SMALL) */}
              {showVideo ? (
                <div className={`flex justify-center lg:justify-end ${isRTL ? "lg:justify-start" : ""}`}>
                  <InlineVideoPlayer
                    src={data.video_url || STATIC_TEST_VIDEO}
                    onClose={() => setShowVideo(false)}
                    isRTL={isRTL}
                    closeLabel={t.close_video}
                  />
                </div>
              ) : (
                /* Big slide number (only on desktop) */
                <div className="hidden lg:block">
                  <div className="flex h-full flex-col items-end justify-between gap-10">
                    <div className={`flex items-baseline gap-3 ${isRTL ? "flex-row-reverse" : ""}`}>
                      <span className="text-[7rem] font-semibold leading-none tracking-tighter text-white/95 drop-shadow-[0_2px_16px_rgba(0,0,0,0.5)] xl:text-[9rem]">
                        {activeNumber}
                      </span>
                      <span className="text-2xl font-light text-white/50 drop-shadow-[0_2px_16px_rgba(0,0,0,0.5)] xl:text-3xl">
                        / {totalNumber}
                      </span>
                    </div>

                    {hasMultiple && (
                      <div className="flex flex-col items-end gap-3">
                        {images.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => goToSlide(index)}
                            aria-label={`Go to slide ${index + 1}`}
                            aria-current={index === currentIndex}
                            className="group flex items-center gap-3"
                          >
                            <span className={`text-[10px] font-medium tabular-nums tracking-widest transition-all duration-300 ${index === currentIndex ? "text-white/90" : "text-white/40 group-hover:text-white/70"}`}>
                              {String(index + 1).padStart(2, "0")}
                            </span>
                            <span className={`h-px transition-all duration-500 ${index === currentIndex ? "w-14 bg-white" : "w-6 bg-white/30 group-hover:w-10 group-hover:bg-white/60"}`} />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* desktop bottom pills */}
      <div className="pointer-events-none absolute inset-x-0 bottom-6 z-20 hidden lg:block">
        <div dir={isRTL ? "rtl" : "ltr"} className="pointer-events-auto mx-auto w-full max-w-7xl px-12">
          <div className="flex items-center justify-end gap-3">
            <FeaturePill icon="map" textone={t.distributors_title} texttwo={t.distributors_sub} href="/locate-us" />
            <FeaturePill icon="question" textone={t.question_title} texttwo={t.question_sub} href="/contact" />
          </div>
        </div>
      </div>

      {/* mobile arrows */}
      {hasMultiple && !showVideo && (
        <div className="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 items-center gap-3 lg:hidden">
          <button onClick={prevSlide} aria-label="Previous slide" className="grid h-10 w-10 place-items-center rounded-full border border-white/15 bg-white/5 text-white backdrop-blur-md transition-all duration-300 hover:bg-white/15 active:scale-95">
            <svg className={`h-4 w-4 ${isRTL ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="text-[11px] font-medium tabular-nums tracking-widest text-white/70">
            {activeNumber} / {totalNumber}
          </span>
          <button onClick={nextSlide} aria-label="Next slide" className="grid h-10 w-10 place-items-center rounded-full border border-white/15 bg-white/5 text-white backdrop-blur-md transition-all duration-300 hover:bg-white/15 active:scale-95">
            <svg className={`h-4 w-4 ${isRTL ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}
    </section>
  );
}